/**
 * Safety report page - submit safety/fraud/abuse reports.
 * 
 * ACT 8: Minimal safety-first report form.
 * 
 * Reports emit audit trail. Privacy-preserving: no personal data required.
 * 
 * TODO(ACT 8): Emit report_started audit event on page load.
 */
import { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { ReportForm } from '@/components/report/ReportForm';

/**
 * Page states.
 */
type PageState = 'form' | 'submitting' | 'success' | 'error';

/**
 * Success state component.
 */
function SuccessState({ reportId }: { reportId: string }) {
  return (
    <div className="success-state" data-testid="report-success">
      <div className="success-icon">✓</div>
      <h2 className="success-title">Report Submitted</h2>
      <p className="success-message">
        Your report has been received. Our safety team will review it.
      </p>
      <p className="success-note">
        Reference: {reportId.slice(0, 16)}...
      </p>
    </div>
  );
}

/**
 * Error state component.
 */
function ErrorState({ message, onRetry }: { message: string; onRetry: () => void }) {
  return (
    <div className="error-state" data-testid="report-error">
      <div className="error-icon">!</div>
      <h2 className="error-title">Submission Failed</h2>
      <p className="error-message">{message}</p>
      <button
        type="button"
        className="btn btn-secondary"
        onClick={onRetry}
        data-testid="retry-button"
      >
        Try Again
      </button>
    </div>
  );
}

/**
 * Report page component.
 */
export function ReportPage() {
  const [searchParams] = useSearchParams();
  const [pageState, setPageState] = useState<PageState>('form');
  const [reportId, setReportId] = useState('');
  const [errorMessage, setErrorMessage] = useState('');

  // Get initial card ID from URL if present
  const initialCardId = searchParams.get('cardId') || undefined;

  // TODO(ACT 8): Emit report_started audit event on page load
  useEffect(() => {
    // Audit TODO: Emit report_started event
    // TODO(ACT 8): await emitAuditEvent('report_started', { initialCardId });
  }, [initialCardId]);

  const handleSuccess = (id: string) => {
    setReportId(id);
    setPageState('success');
  };

  const handleError = (error: string) => {
    setErrorMessage(error);
    setPageState('error');
  };

  const handleRetry = () => {
    setPageState('form');
    setErrorMessage('');
  };

  // Success state
  if (pageState === 'success') {
    return (
      <div className="page">
        <div className="page-header">
          <h1 className="page-title">Safety Report</h1>
        </div>
        <SuccessState reportId={reportId} />
        <style>{reportPageStyles}</style>
      </div>
    );
  }

  // Error state
  if (pageState === 'error') {
    return (
      <div className="page">
        <div className="page-header">
          <h1 className="page-title">Safety Report</h1>
        </div>
        <ErrorState message={errorMessage} onRetry={handleRetry} />
        <style>{reportPageStyles}</style>
      </div>
    );
  }

  // Form state
  return (
    <div className="page">
      <div className="page-header">
        <h1 className="page-title">Safety Report</h1>
        <p className="page-description">
          Report safety concerns, suspected fraud, or inappropriate behavior.
          Your identity remains protected.
        </p>
      </div>

      <div className="report-content" data-testid="report-content">
        <ReportForm
          initialCardId={initialCardId}
          onSuccess={handleSuccess}
          onError={handleError}
        />
      </div>

      <style>{reportPageStyles}</style>
    </div>
  );
}

/**
 * Report page styles.
 */
const reportPageStyles = `
  .report-content {
    margin-top: var(--spacing);
  }
  .success-state,
  .error-state {
    text-align: center;
    padding: var(--spacing-lg);
    margin-top: var(--spacing);
  }
  .success-icon {
    width: 64px;
    height: 64px;
    border-radius: 50%;
    background: var(--color-success-bg, #e6f4ea);
    color: var(--color-success, #1e7e34);
    font-size: 2rem;
    display: flex;
    align-items: center;
    justify-content: center;
    margin: 0 auto var(--spacing);
  }
  .error-icon {
    width: 64px;
    height: 64px;
    border-radius: 50%;
    background: var(--color-error-bg, #ffebee);
    color: var(--color-error, #d32f2f);
    font-size: 2rem;
    font-weight: bold;
    display: flex;
    align-items: center;
    justify-content: center;
    margin: 0 auto var(--spacing);
  }
  .success-title,
  .error-title {
    font-size: 1.25rem;
    font-weight: 600;
    margin-bottom: var(--spacing);
  }
  .success-message,
  .error-message {
    color: var(--color-text-muted);
    margin-bottom: var(--spacing);
    line-height: 1.5;
  }
  .success-note {
    font-size: 0.8rem;
    color: var(--color-text-muted);
    font-family: monospace;
  }
  .btn {
    padding: 12px 24px;
    border-radius: var(--radius);
    font-size: 0.9rem;
    font-weight: 500;
    cursor: pointer;
    border: none;
  }
  .btn-secondary {
    background: var(--color-surface);
    color: var(--color-text);
    border: 1px solid var(--color-border);
  }
  .btn-secondary:hover {
    background: var(--color-surface-hover, #f5f5f5);
  }
`;
